class ModalNew extends HTMLElement {
  constructor() {
    super();
    // FOR TESTING

    this.debug = false;
    
    this.type = null;
    this.triggerType = null;
    this.closeButton = null;
    this.modalContent = null;
    this._boundToggleModal = null
    this._boundCloseModal = null;
    this._boundHandleOverlayClick = null;
    this._boundHandleMouseDown = null;
    this._boundHandleMouseUp = null;
    this._isDragging = false;
    this._mouseDownTime = 0;
    
    // Focus trap properties
    this._focusTrapBound = null;
    this._focusableElements = [];
    this._firstFocusable = null;
    this._lastFocusable = null;
    
    // Keyboard mode tracking
    this._isKeyboardMode = false;
  }

  connectedCallback() {
    this.triggerType = this.dataset.triggerType;
    this.trigger = document.querySelectorAll(`[modal-trigger="${this.triggerType}"]`);
    this.closeButton = this.querySelector("[data-modal-close]");
    this.modalContent = this.querySelector("[data-modal-content]");
    this._boundToggleModal = this.toggleModal.bind(this);
    this._boundCloseModal = this.closeModal.bind(this);
    this._boundHandleOverlayClick = this._handleOverlayClick.bind(this);
    this._boundHandleMouseDown = this._handleMouseDown.bind(this);
    this._boundHandleMouseUp = this._handleMouseUp.bind(this);
    
    this.init();
  }

  init() {
    if (this.trigger.length > 0) {
      this.trigger.forEach(trigger => {
        trigger.addEventListener("click", this._boundToggleModal);
      });
    }

    if (this.closeButton) {
      this.closeButton.addEventListener("click", this._boundCloseModal);
    }

    this.addEventListener("click", this._boundHandleOverlayClick);
    this.addEventListener("mousedown", this._boundHandleMouseDown);
    this.addEventListener("mouseup", this._boundHandleMouseUp);
    
    // Add Escape key handler and keyboard mode detection
    this._boundHandleEscape = this._handleEscape.bind(this);
    this._boundHandleKeydown = this._handleKeydown.bind(this);
    this._boundHandleMousedown = this._handleMouseclick.bind(this);
    
    document.addEventListener("keydown", this._boundHandleEscape);
    document.addEventListener("keydown", this._boundHandleKeydown);
    // Use capture phase to ensure it runs before focus events
    document.addEventListener("mousedown", this._boundHandleMouseclick, true);

    if (this.debug) {
      this.showModal();
    }
  }

  _handleOverlayClick(event) {
    // Only close if clicking directly on the modal overlay (not dragging from text selection)
    if (event.target === this && this.classList.contains("modal--open")) {
      // Don't close if user was dragging (text selection)
      if (!this._isDragging) {
        this.closeModal();
      }
    }
  }

  _handleMouseDown(event) {
    // Track when mouse is pressed down
    this._mouseDownTime = Date.now();
    this._isDragging = false;
    
    // Also disable keyboard mode on any mousedown
    if (this._isKeyboardMode) {
      this._isKeyboardMode = false;
      this.classList.remove('keyboard-mode');
    }
  }

  _handleMouseUp(event) {
    const timeRange = 200;
    // Check if this was a drag operation (mouse was held down for a while or moved)
    const timeDiff = Date.now() - this._mouseDownTime;
    if (timeDiff > timeRange) { // If mouse was held down for more than 100ms, consider it a drag
      this._isDragging = true;
    }
    
    // Reset dragging flag after a short delay
    setTimeout(() => {
      this._isDragging = false;
    }, timeRange);
  }

  _lockScroll() {
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
  }

  _unlockScroll() {
    // Restore body scroll when modal is closed
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
  }

  closeModal() {
    this.classList.remove("modal--open");
    this.classList.add("modal--closing");
    this._unlockScroll();
    this._deactivateFocusTrap();

    setTimeout(() => {
      this.classList.remove("modal--closing");
      this.classList.add("modal--closed");
    }, 500);
  }

  showModal() {
    this.classList.remove("modal--closed", "modal--closing");
    this.classList.add("modal--open");
    this._lockScroll();
    
    // Wait for content to be visible and select components to initialize before setting up focus trap
    setTimeout(() => {
      this._activateFocusTrap();
    }, 300);
  }

  toggleModal() {
    this.classList.contains("modal--open") 
      ? this.closeModal() : this.showModal();
  }

  disconnectedCallback() {
    if (this.trigger) {
      this.trigger.forEach(t => t.removeEventListener("click", this._boundToggleModal));
    }

    if (this.closeButton) {
      this.closeButton.removeEventListener("click", this._boundCloseModal);
    }

    this.removeEventListener("click", this._boundHandleOverlayClick);
    this.removeEventListener("mousedown", this._boundHandleMouseDown);
    this.removeEventListener("mouseup", this._boundHandleMouseUp);
    
    if (this._boundHandleEscape) {
      document.removeEventListener("keydown", this._boundHandleEscape);
    }
    
    if (this._boundHandleKeydown) {
      document.removeEventListener("keydown", this._boundHandleKeydown);
    }
    
    if (this._boundHandleMouseclick) {
      document.removeEventListener("mousedown", this._boundHandleMouseclick, true);
    }
    
    this._deactivateFocusTrap();
  }

  _handleEscape(event) {
    // Close modal on Escape key if it's open
    if (event.key === 'Escape' && this.classList.contains("modal--open")) {
      this.closeModal();
    }
  }

  _handleKeydown(event) {
    // Enable keyboard mode when Tab is pressed
    if (event.key === 'Tab') {
      this._isKeyboardMode = true;
      this.classList.add('keyboard-mode');
    }
  }

  _handleMouseclick() {
    // Disable keyboard mode when mouse is used
    if (this._isKeyboardMode) {
      this._isKeyboardMode = false;
      this.classList.remove('keyboard-mode');
    }
  }

  _getFocusableElements() {
    if (!this.modalContent) return [];
    
    // Get all focusable elements within the modal content
    const selector = [
      'button:not([disabled])',
      '[href]',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex^="-"])'
    ].join(', ');
    
    const elements = Array.from(this.modalContent.querySelectorAll(selector));
    
    return elements.filter((el) => {
      // Exclude select-component elements themselves (only include their triggers)
      if (el.tagName === 'SELECT-COMPONENT') {
        return false;
      }
      
      // Exclude select-component dropdowns
      if (el.classList.contains('select-component__dropdown')) {
        return false;
      }
      
      // Filter out hidden elements
      const isHidden = el.offsetWidth === 0 || 
                       el.offsetHeight === 0 ||
                       window.getComputedStyle(el).visibility === 'hidden' ||
                       window.getComputedStyle(el).display === 'none';
      
      return !isHidden;
    });
  }

  _activateFocusTrap() {
    // Get all focusable elements
    this._focusableElements = this._getFocusableElements();
    
    if (this._focusableElements.length === 0) return;
    
    this._firstFocusable = this._focusableElements[0];
    this._lastFocusable = this._focusableElements[this._focusableElements.length - 1];
    
    // Bind the handler
    this._focusTrapBound = this._handleFocusTrap.bind(this);
    
    // Add event listener for Tab key
    document.addEventListener('keydown', this._focusTrapBound);
  }

  _deactivateFocusTrap() {
    // Remove event listener
    if (this._focusTrapBound) {
      document.removeEventListener('keydown', this._focusTrapBound);
      this._focusTrapBound = null;
    }
    
    // Clear references
    this._focusableElements = [];
    this._firstFocusable = null;
    this._lastFocusable = null;
  }

  _handleFocusTrap(event) {
    // Only handle Tab key
    if (event.key !== 'Tab') return;
    
    // Recalculate focusable elements in case content changed
    this._focusableElements = this._getFocusableElements();
    
    if (this._focusableElements.length === 0) return;
    
    this._firstFocusable = this._focusableElements[0];
    this._lastFocusable = this._focusableElements[this._focusableElements.length - 1];
    
    const activeElement = document.activeElement;
    
    // If focus is not within the modal content, focus the first element
    if (!this.modalContent.contains(activeElement)) {
      event.preventDefault();
      this._firstFocusable.focus();
      return;
    }
    
    // Check if we're at the first or last element
    if (event.shiftKey) {
      // Shift + Tab (backwards)
      if (activeElement === this._firstFocusable) {
        event.preventDefault();
        this._lastFocusable.focus();
      }
    } else {
      // Tab (forwards)
      if (activeElement === this._lastFocusable) {
        event.preventDefault();
        this._firstFocusable.focus();
      }
    }
  }
}

customElements.define("modal-new", ModalNew);